import React, { useState, useEffect } from 'react';
import { Star, CheckCircle, User } from 'lucide-react';
import unifiedAuthService from '../services/unifiedAuth';
import VerificationNotification from './VerificationNotification';

// Centralized API base URL for Azure Functions backend
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071';

export const ReviewSystem = ({ 
  onClose, 
  selectedService, 
  isLoggedIn = false, 
  userProfile = null, 
  onLoginRequired = null 
}) => {
  const [verificationNotification, setVerificationNotification] = useState(null);

  // Enhanced Verification Functions
  const sendPhoneVerificationCode = async (phoneNumber) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/verification/phone/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phoneNumber })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send verification code');
      }

      // Show verification code in notification (development only)
      if (data.debug_code) {
        setVerificationNotification({
          code: data.debug_code,
          type: 'phone'
        });
        console.log(`📱 Phone verification code sent to ${phoneNumber}: ${data.debug_code}`);
      }
      return data;
    } catch (error) {
      throw new Error(error.message || 'Failed to send verification code');
    }
  };

  const sendEmailVerificationCode = async (emailAddress) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/verification/email/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ emailAddress })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send verification code');
      }

      // Show verification code in notification (development only)
      if (data.debug_code) {
        setVerificationNotification({
          code: data.debug_code,
          type: 'email'
        });
        console.log(`📧 Email verification code sent to ${emailAddress}: ${data.debug_code}`);
      }
      return data;
    } catch (error) {
      throw new Error(error.message || 'Failed to send verification code');
    }
  };
  const [reviewData, setReviewData] = useState({
    rating: 0,
    comment: '',
    verificationCode: '',
    isVerified: false,
    reviewType: '', // 'service' or 'advertisement'
    serviceType: '', // specific service type
    region: '' // 'north_america', 'europe', 'asia', 'other'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Debug logging on component mount
  useEffect(() => {
    console.log('🔍 ReviewSystem props:', {
      isLoggedIn,
      userProfile,
      selectedService,
      unifiedAuthIsAuthenticated: unifiedAuthService.isAuthenticated(),
      unifiedAuthCurrentUser: unifiedAuthService.getCurrentUser()
    });
  }, [isLoggedIn, userProfile, selectedService]);

  // Automatically verify authenticated users
  useEffect(() => {
    if (isLoggedIn && userProfile) {
      setReviewData(prev => ({ ...prev, isVerified: true }));
    }
  }, [isLoggedIn, userProfile]);

  const handleRatingChange = (rating) => {
    setReviewData(prev => ({ ...prev, rating }));
  };

  const handleSubmitReview = async () => {
    // Check if user is logged in
    if (!isLoggedIn || !userProfile) {
      console.error('❌ User not logged in:', { isLoggedIn, userProfile });
      if (onLoginRequired) {
        onLoginRequired();
      } else {
        alert('Please log in to submit a review');
      }
      return;
    }

    console.log('🔐 Current authentication state:', {
      isLoggedIn,
      userProfile,
      token: unifiedAuthService.getAuthToken() ? 'Present' : 'Missing',
      authenticated: unifiedAuthService.isAuthenticated()
    });

    // Validate review data
    if (reviewData.rating === 0) {
      alert('Please select a rating');
      return;
    }
    if (!reviewData.reviewType) {
      alert('Please select what you are reviewing (Service or Advertisement)');
      return;
    }
    if (!reviewData.region) {
      alert('Please select your region');
      return;
    }
    if (!reviewData.comment.trim()) {
      alert('Please write a review comment');
      return;
    }
    if (!reviewData.isVerified) {
      alert('Please verify your purchase with a valid code');
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare review payload for save-complete-review endpoint
      const reviewPayload = {
        selectedUser: {
          instagram: userProfile.identifiers?.instagramUsername || userProfile.username,
          email: userProfile.identifiers?.email || userProfile.email
        },
        rating: reviewData.rating,
        reviewType: reviewData.reviewType,
        serviceType: reviewData.serviceType || 'general',
        region: reviewData.region,
        comment: reviewData.comment,
        targetBusiness: {
          instagram: selectedService?.instagramHandle || selectedService?.username,
          email: selectedService?.email || null,
          name: selectedService?.serviceName || selectedService?.displayName || selectedService?.name
        },
        verification: {
          isVerified: reviewData.isVerified,
          method: 'authenticated', // Using unified auth
          emailAddress: userProfile.identifiers?.email || userProfile.email
        }
      };

      console.log('🔄 Submitting review:', reviewPayload);

      // Submit review using authenticated request
      const result = await unifiedAuthService.authenticatedRequest(
        '/api/save-complete-review',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(reviewPayload)
        }
      );

      console.log('✅ Review submission result:', result);

      const reviewTypeText = reviewData.reviewType === 'service' ? 'Service' : 'Advertisement';
      const regionText = {
        'north_america': 'North America',
        'europe': 'Europe', 
        'asia': 'Asia',
        'other': 'Other'
      }[reviewData.region];

      alert(`Review submitted successfully!\n\nRating: ${reviewData.rating}/5\nType: ${reviewTypeText}\nRegion: ${regionText}\nVerified: ✅\nService: ${selectedService?.instagramHandle}\nUser: ${userProfile?.username || userProfile?.identifiers?.instagramUsername}`);
      
      // Reset form
      setReviewData({ rating: 0, comment: '', verificationCode: '', isVerified: false, reviewType: '', region: '', serviceType: '' });
      onClose();
    } catch (error) {
      console.error('Review submission error:', error);
      alert(`Error submitting review: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!selectedService) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      {/* Development Mode Verification Code Display */}
      {verificationNotification && (
        <VerificationNotification 
          code={verificationNotification.code}
          type={verificationNotification.type}
          onClose={() => setVerificationNotification(null)}
        />
      )}
      
      <div className="bg-white rounded-lg max-w-2xl w-full p-6 relative">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Write a Review</h2>
              <p className="text-gray-600 mt-1">Share your experience with {selectedService.instagramHandle}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6">
          {!isLoggedIn ? (
            /* Login Required Section */
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Login Required</h3>
              <p className="text-gray-600 mb-6">Please log in to write a review</p>
              <button
                onClick={() => {
                  if (onLoginRequired) {
                    onLoginRequired();
                  }
                }}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-all"
              >
                Sign In / Sign Up
              </button>
            </div>
          ) : (
            /* Review Form */
            <div className="space-y-6">
              {/* User Info */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">
                      {userProfile?.profile?.name?.charAt(0) || userProfile?.identifiers?.instagramUsername?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">
                      {userProfile?.identifiers?.instagramUsername ? 
                        `@${userProfile.identifiers.instagramUsername}` : 
                        userProfile?.identifiers?.email || 'User'
                      }
                    </p>
                    <div className="flex items-center gap-1">
                      {reviewData.isVerified ? (
                        <CheckCircle className="text-green-500" size={14} />
                      ) : (
                        <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                      )}
                      <span className="text-xs text-gray-500">
                        {reviewData.isVerified ? 'Authenticated User' : 'Verification Required'}
                      </span>
                    </div>
                  </div>
                </div>
                <span className="text-xs text-gray-500">Logged In</span>
              </div>

              {/* Service Info */}
              <div className="p-4 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">Reviewing:</h4>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-lg">
                      {selectedService.instagramHandle.charAt(1).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">{selectedService.serviceName}</p>
                    <p className="text-purple-600 text-sm">{selectedService.instagramHandle}</p>
                  </div>
                </div>
              </div>

              {/* Rating */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Your Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      onClick={() => handleRatingChange(star)}
                      className={`p-2 rounded-lg transition-all ${
                        star <= reviewData.rating
                          ? 'bg-yellow-100 text-yellow-600'
                          : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                      }`}
                    >
                      <Star
                        size={24}
                        className={star <= reviewData.rating ? 'fill-current' : ''}
                      />
                    </button>
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {reviewData.rating > 0 ? `${reviewData.rating} out of 5 stars` : 'Select your rating'}
                </p>
              </div>

              {/* Review Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">What are you reviewing?</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setReviewData(prev => ({ ...prev, reviewType: 'service' }))}
                    className={`p-4 border-2 rounded-lg transition-all ${
                      reviewData.reviewType === 'service'
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-center">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                        <span className="text-purple-600 font-bold">S</span>
                      </div>
                      <p className="font-semibold">Service</p>
                      <p className="text-xs text-gray-500 mt-1">Coaching, consulting, courses</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setReviewData(prev => ({ ...prev, reviewType: 'advertisement' }))}
                    className={`p-4 border-2 rounded-lg transition-all ${
                      reviewData.reviewType === 'advertisement'
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-center">
                      <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2">
                        <span className="text-orange-600 font-bold">A</span>
                      </div>
                      <p className="font-semibold">Advertisement</p>
                      <p className="text-xs text-gray-500 mt-1">Products, brands, sponsorships</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Region Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Your Region</label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { id: 'north_america', label: 'North America', icon: '🇺🇸' },
                    { id: 'europe', label: 'Europe', icon: '🇪🇺' },
                    { id: 'asia', label: 'Asia', icon: '🌏' },
                    { id: 'other', label: 'Other', icon: '🌍' }
                  ].map(region => (
                    <button
                      key={region.id}
                      type="button"
                      onClick={() => setReviewData(prev => ({ ...prev, region: region.id }))}
                      className={`p-3 border-2 rounded-lg transition-all ${
                        reviewData.region === region.id
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{region.icon}</span>
                        <span className="font-medium">{region.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Comment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Review</label>
                <textarea
                  value={reviewData.comment}
                  onChange={(e) => setReviewData(prev => ({ ...prev, comment: e.target.value }))}
                  placeholder="Share your experience with this service..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows={4}
                />
              </div>

              {/* Verification Status */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="text-green-600" size={20} />
                  <div>
                    <h4 className="font-semibold text-green-800">Verified User</h4>
                    <p className="text-sm text-green-700">
                      You are logged in and verified to submit reviews.
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmitReview}
                disabled={!reviewData.isVerified || reviewData.rating === 0 || !reviewData.comment.trim() || isSubmitting}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 